import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { ExactAccountForm, isExactAccountFormInvalid } from "modules/DealerDetails/components/Invoicing/components/ExactAccountModal/components";
import "modules/DealerDetails/components/Invoicing/components/ExactAccountModal/ExactAccountModal.scss";
import { useSaveExactAccountMutation } from "modules/DealerDetails/components/Invoicing/components/ExactAccountModal/hooks";

import type { DealerID, DealerLocationID } from "models";
import type { ExactAccountFormPayload } from "modules/DealerDetails/components/Invoicing/components/ExactAccountModal/components";
import type { ExactAccount } from "modules/DealerDetails/components/Invoicing/hooks";

const EXACT_ACCOUNT_FORM_ID = "exact-account-form";
const FormHelper = createFormHelper<ExactAccountFormPayload>();

type ExactAccountModalProps = {
  isOpen: boolean;
  account?: ExactAccount | null;
  dealerId?: DealerID;
  locationId?: DealerLocationID;
  onClose: () => void;
  onSaved: (exactAccountId: string) => void;
};

const getDefaultFormValues = (account?: ExactAccount | null): ExactAccountFormPayload => ({
  exact_invoicing_company_name: account?.Name ?? "",
  exact_invoicing_country: account?.Country ?? "",
  exact_invoicing_email: account?.Contact?.Email ?? "",
  exact_invoicing_city: account?.City ?? "",
  exact_vat_number: account?.VATNumber ?? "",
  exact_invoicing_address: account?.AddressLine1 ?? "",
  exact_invoicing_initials: account?.Contact?.Initials ?? "",
  exact_invoicing_last_name: account?.Contact?.LastName ?? "",
  exact_invoicing_zip_code: account?.Postcode ?? "",
  exact_chamber_of_commerce: account?.ChamberOfCommerce ?? "",
  exact_invoicing_language: account?.Language ?? "",
  exact_account_code: account?.Code ?? ""
});

export const ExactAccountModal = ({ isOpen, account, dealerId, locationId, onClose, onSaved }: ExactAccountModalProps) => {
  const { t } = useTranslation();

  const { mutate: saveExactAccount, isPending: isSaveExactAccountPending } = useSaveExactAccountMutation();
  const formMethods = FormHelper.useForm({ values: getDefaultFormValues(account), mode: "all" });

  const handleClose = () => {
    formMethods.reset(getDefaultFormValues());
    onClose();
  };

  const handleSubmit = ({ exact_account_code: _code, ...values }: ExactAccountFormPayload) => {
    if (isExactAccountFormInvalid(values)) return;

    saveExactAccount(
      { ...values, dealer_id: dealerId, dealer_location_id: locationId },
      {
        onSuccess: exactAccountId => {
          onSaved(exactAccountId);
          handleClose();
        }
      }
    );
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.stopPropagation();
    formMethods.handleSubmit(handleSubmit)(event);
  };

  return (
    <Modal open={isOpen} size="small" closeOnDimmerClick={false} onClose={handleClose} className="ExactAccountModal">
      <FormHelper.FormProvider {...formMethods}>
        <Modal.Header>
          <div>{account ? t("v8_update_exact_account", "Update Exact account") : t("v8_create_exact_account", "Create Exact account")}</div>
          <Button className="modal-close-btn" basic type="button" onClick={handleClose}>
            <Icon className="xmark" />
          </Button>
        </Modal.Header>

        <Modal.Content>
          <Form id={EXACT_ACCOUNT_FORM_ID} onSubmit={handleFormSubmit} disabled={isSaveExactAccountPending}>
            <ExactAccountForm />
          </Form>
        </Modal.Content>

        <Modal.Actions>
          <div className="action-buttons-container">
            <Button type="button" className="light" onClick={handleClose} disabled={isSaveExactAccountPending}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Button>
            <Button type="submit" form={EXACT_ACCOUNT_FORM_ID} color="green" disabled={isSaveExactAccountPending} loading={isSaveExactAccountPending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Button>
          </div>
        </Modal.Actions>
      </FormHelper.FormProvider>
    </Modal>
  );
};
